import React from 'react';
import {
  AgnesModal,
  AgnesButton,
  AgnesInput,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { useUsuariosStore } from '@/stores/useUsuariosStore';
import ServiceUsuarios from '@/services/ServiceUsuarios';

const SELECT_TRIGGER = 'w-full';
const ROLES = ['SUPER_ADMINISTRADOR', 'ADMINISTRADOR', 'ANALISTA', 'USUARIO'];

function respostaExiste(r: { data?: unknown }): boolean {
  const d = r?.data as boolean | { exists?: boolean; existe?: boolean } | null | undefined;
  return (
    d === true || (typeof d === 'object' && d !== null && (d.exists === true || d.existe === true))
  );
}

type Usuario = any;

interface Props {
  open: boolean;
  onClose: () => void;
  usuario?: Usuario | null; // presente = edição
}

export default function UsuarioFormModal({ open, onClose, usuario }: Props) {
  const editando = Boolean(usuario);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [role, setRole] = React.useState('USUARIO');
  const [emailErro, setEmailErro] = React.useState('');
  const [usuarioErro, setUsuarioErro] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => {
    setFirstName(usuario?.first_name ?? '');
    setLastName(usuario?.last_name ?? '');
    setEmail(usuario?.email ?? '');
    setUsername(usuario?.username ?? '');
    setPassword('');
    setRole(usuario?.roles?.[0]?.name ?? 'USUARIO');
    setEmailErro('');
    setUsuarioErro('');
    setSalvando(false);
  }, [usuario, open]);

  const podeSalvar = firstName && lastName && email && role && (editando || password.length >= 8);

  async function salvar() {
    setSalvando(true);
    try {
      if (editando) {
        await useUsuariosStore.getState().editar(usuario.id, {
          first_name: firstName,
          last_name: lastName,
          email,
          kc_role: role,
        });
        onClose();
        return;
      }

      const login = username || email;
      const [emailExiste, loginExiste] = await Promise.all([
        ServiceUsuarios.existeEmail(email)
          .then(respostaExiste)
          .catch(() => false),
        ServiceUsuarios.existeLogin(login)
          .then(respostaExiste)
          .catch(() => false),
      ]);
      if (emailExiste) setEmailErro('E-mail já cadastrado');
      if (loginExiste) setUsuarioErro('Usuário já cadastrado');
      if (emailExiste || loginExiste) return;

      await useUsuariosStore.getState().criar({
        first_name: firstName,
        last_name: lastName,
        email,
        username: login,
        password,
        kc_role: role,
      });
      onClose();
    } catch {
      // store já notificou; mantém o modal aberto
    } finally {
      setSalvando(false);
    }
  }

  return (
    <AgnesModal
      open={open}
      title={editando ? 'Editar usuário' : 'Novo usuário'}
      maxWidth="sm"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton variant="text" onClick={onClose}>
            Cancelar
          </AgnesButton>
          <AgnesButton variant="primary" disabled={!podeSalvar || salvando} onClick={salvar}>
            Salvar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <AgnesInput
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Nome *"
        />
        <AgnesInput
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Sobrenome *"
        />
        <AgnesInput
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailErro) setEmailErro('');
          }}
          placeholder="E-mail *"
          error={emailErro}
        />
        <AgnesInput
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            if (usuarioErro) setUsuarioErro('');
          }}
          placeholder="Usuário"
          disabled={editando}
          error={usuarioErro}
        />
        {!editando && (
          <AgnesInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha * (mín. 8)"
          />
        )}
        <AgnesSelect value={role} onValueChange={setRole}>
          <AgnesSelectTrigger className={SELECT_TRIGGER}>
            <AgnesSelectValue placeholder="Perfil *" />
          </AgnesSelectTrigger>
          <AgnesSelectContent>
            {ROLES.map((r) => (
              <AgnesSelectItem key={r} value={r}>
                {r}
              </AgnesSelectItem>
            ))}
          </AgnesSelectContent>
        </AgnesSelect>
      </div>
    </AgnesModal>
  );
}
